/**
 * Firebase Configuration
 * Initialize Firebase app with project credentials
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth, setPersistence, browserLocalPersistence } from 'firebase/auth';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
};

// Validate Firebase configuration
const isFirebaseConfigured = () => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId
  );
};

// Initialize Firebase
// IMPORTANT: Skip initialization inside iframes (e.g., heatmap preview).
// Firebase SDK uses IndexedDB (shared same-origin) and initializing it in an
// iframe can corrupt the parent window's auth session, causing unexpected logouts.
let app: FirebaseApp | null = null;
let auth: Auth | null = null;

const isInIframe = typeof window !== 'undefined' && window.self !== window.top;

if (isInIframe) {
  // No-op: iframe must never touch Firebase
} else if (isFirebaseConfigured()) {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);

    // Set persistence to LOCAL (keeps user logged in even after browser close)
    setPersistence(auth, browserLocalPersistence).catch(() => {
      // Persistence setup failed - app will continue with default behavior
    });
  } catch (error) {
    console.error('❌ Firebase initialization error:', error);
  }
} else {
  console.warn('⚠️  Firebase not configured. Please set environment variables.');
}

export { app, auth, firebaseConfig, isFirebaseConfigured };
